import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isIconAccessoryProp } from '#pplx/frontend/aether/components/Accessory/isIconAccessoryProp.js';
import { getButtonClasses } from '#pplx/frontend/aether/components/Button/classes.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { useTrackOnClick } from '#pplx/frontend/aether/internal/useTrackOnClick.js';
import { LinkPrimitive } from '#pplx/frontend/aether/primitives/LinkPrimitive/index.js';
import { asAutoInstrumented } from '#pplx/frontend/framework/instrumentation/types.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { useMemo } from 'react';
function getInlineLinkClasses({ muted, bold }) {
    return cva('cursor-pointer decoration-1 underline-offset-1', {
        variants: {
            muted: {
                false: 'text-super-primary hover:underline',
                true: 'underline hover:no-underline',
            },
            bold: {
                false: '',
                true: 'font-semibold',
            },
        },
    })({ muted, bold });
}
const getLinkClasses = ({ variant, muted, bold, ...props }) => {
    if (variant === 'inline') {
        return getInlineLinkClasses({ muted, bold });
    }
    return getButtonClasses({ variant: variant, ...props });
};
/**
 * Polymorphic link component that supports inline text links, button-style
 * links, and icon-only link buttons. Defaults to the "inline" variant.
 *
 * @category components
 * @keywords link, components, polymorphic, component, that, supports, inline,
 *   text, links, button-style, icon-only, buttons, defaults, variant, href,
 *   disabled, leadingicon, trailingicon, icon, button, tooltip
 * @whenToUse Use Link for navigation to other pages, sections, or external
 *   URLs. It operates in three modes: inline links for text-embedded
 *   navigation that blend with body content, icon button links for compact
 *   navigational icons with automatic tooltips, and text button links for
 *   standalone navigational actions styled like buttons. Choose the inline
 *   variant for in-content links and button-style variants when the
 *   navigation should visually resemble a button.
 * @guidelineDo
 *   - Always use Link as the default choice for navigational anchor elements — it is the core
 *     component that provides inline, button-style, and icon-only variants with built-in
 *     accessibility and analytics. Prefer Link over LinkPrimitive in all standard cases
 *   - Use the inline variant (default) for links within body text and the button-style variants
 *     (primary, secondary, tonal, text) for standalone navigational actions
 *   - Use the muted prop on inline links that should blend with surrounding text color, such as
 *     legal or terms links, while maintaining an underline for discoverability
 *   - Provide an aria-label on icon-only button links — it is required by the type system and
 *     drives the automatic Tooltip
 *   - Use the hardNav prop when the link should perform a full page navigation instead of a
 *     client-side route transition
 *   - Use the track prop to fire analytics when the link is clicked without adding custom click
 *     handlers. The onTrackEvent callback is deprecated in favor of track, which mirrors the
 *     Aether Button convention
 *   - Register your app's routing Link component in AetherProvider via the linkComponent prop so
 *     all Aether Link instances use your router. Example: <AetherProvider linkComponent={NextLink}>.
 *   - Use the Aether Link component for all navigation — it delegates to the registered
 *     linkComponent, ensuring consistent routing behavior.
 * @guidelineDont
 *   - Don't reach for LinkPrimitive when Link can do the job — Link covers inline, button-style,
 *     and icon-only link patterns with built-in accessibility. Only use LinkPrimitive as a last
 *     resort for truly custom one-off implementations
 *   - Don't set disabled on inline links — inline links forbid the disabled prop by design because
 *     disabled inline links break semantic expectations, accessibility, and visual clarity within
 *     text flow
 *   - Don't set size, fullWidth, pill, or rounded on inline links — inline links are designed to
 *     inherit the surrounding text size and flow naturally within content
 *   - Don't use aria-label on text button links or inline links — they derive their accessible
 *     name from their visible text content
 *   - Don't use Link for actions that change state without navigating — use Button instead for
 *     operations like form submission, toggling, or deleting
 *   - Don't use raw <a> tags for internal navigation — they bypass the router and cause full page
 *     reloads.
 *   - Don't use framework-specific link components (e.g., next/link, react-router Link) directly
 *     alongside Aether — register once in AetherProvider instead.
 * @accessibility
 *   - Icon-only button links automatically wrap in a Tooltip that displays the aria-label (or
 *     title), providing a visible label for sighted users and an accessible name for screen
 *     readers
 *   - Disabled button-style links render as a <span> with role='link' and aria-disabled='true',
 *     maintaining semantic meaning while preventing navigation
 *   - Inline links are intentionally never disableable — the type system enforces this to prevent
 *     confusing screen reader experiences where a link appears in text flow but is not interactive
 *   - Inline links use underline styling for discoverability: default inline links show underline
 *     on hover, while muted inline links show underline by default and remove it on hover
 *   - The component uses LinkPrimitive which renders as an <a> element, ensuring proper link
 *     semantics and native keyboard activation via Enter key
 * @relatedComponents
 *   - button
 *   - icon
 *   - tooltip
 */
export function Link({ ref, href, children, icon, 'aria-label': ariaLabel, variant = 'inline', size = 'default', disabled = false, target, rel, title, fullWidth = false, leadingAccessory, trailingAccessory, rounded = false, pill = false, muted = false, bold = false, hardNav = false, onTrackEvent, track, tooltipSubtitle, __dangerousHtmlProps = {}, 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    const targetRef = useMeasureableHandle(ref);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the LinkPrimitive.
    const __radixProps = useRadixProps(props);
    const defaultTrackOptions = useMemo(() => ({ event: 'link selected', data: asAutoInstrumented({ href }) }), [href]);
    const trackedOnTrackEvent = useTrackOnClick(track, defaultTrackOptions, onTrackEvent);
    const computedChildren = useMemo(() => {
        if (icon) {
            return _jsx(Icon, { icon: icon, size: size });
        }
        return (_jsxs(_Fragment, { children: [leadingAccessory && (_jsx("div", { className: classNames('inline-flex items-center', 
                    // below negative margin is to optically align the icon with the link's internal padding.
                    '-ml-1'), children: isIconAccessoryProp(leadingAccessory) ? (_jsx(Icon, { icon: leadingAccessory, size: size })) : (leadingAccessory) })), _jsx("span", { className: classNames(
                    // Trims both the start/end of the text box (removes baked in vertical padding provided by the font itself)
                    'text-box-trim-both', {
                        'pl-1': leadingAccessory,
                        'pr-1': trailingAccessory,
                    }), children: children }), trailingAccessory && (_jsx("div", { className: classNames('inline-flex items-center', 
                    // below negative margin is to optically align the icon with the link's internal padding.
                    '-mr-1'), children: isIconAccessoryProp(trailingAccessory) ? (_jsx(Icon, { icon: trailingAccessory, size: size })) : (trailingAccessory) }))] }));
    }, [icon, leadingAccessory, size, children, trailingAccessory]);
    if (variant === 'inline') {
        return (_jsx(LinkPrimitive, { ref: targetRef, ...__radixProps, href: href, target: target, rel: rel, title: title, className: getLinkClasses({ variant: 'inline', muted, bold }), onTrackEvent: trackedOnTrackEvent, hardNav: hardNav, ...__dangerousHtmlProps, children: computedChildren }));
    }
    if (disabled) {
        return (_jsx("span", { ref: targetRef, ...__radixProps, role: "link", className: getLinkClasses({
                variant,
                size,
                disabled,
                fullWidth,
                rounded,
                pill,
                bold: false,
                iconOnly: Boolean(icon),
            }), "aria-disabled": "true", "aria-label": ariaLabel, children: computedChildren }));
    }
    if (icon) {
        const linkElement = (_jsx(LinkPrimitive, { ref: targetRef, ...__radixProps, href: href, target: target, rel: rel, className: getLinkClasses({
                variant,
                size,
                disabled,
                fullWidth: false,
                rounded,
                pill: false,
                bold: false,
                iconOnly: true,
            }), onTrackEvent: trackedOnTrackEvent, "aria-label": ariaLabel, hardNav: hardNav, ...__dangerousHtmlProps, children: computedChildren }));
        if (disabled) {
            // Disabled elements are not focusable so there's no need to wrap in a tooltip.
            // Consumers should apply some message elsewhere in the UI to indicate why the link is disabled.
            return linkElement;
        }
        return (_jsx(Tooltip, { content: title || ariaLabel, subtitle: tooltipSubtitle, children: linkElement }));
    }
    return (_jsx(LinkPrimitive, { ref: targetRef, ...__radixProps, href: href, target: target, rel: rel, title: title, className: getLinkClasses({
            variant,
            size,
            disabled,
            fullWidth,
            pill,
            bold: false,
        }), onTrackEvent: trackedOnTrackEvent, hardNav: hardNav, ...__dangerousHtmlProps, children: computedChildren }));
}
//# sourceMappingURL=index.js.map